import { useLocation, useNavigate } from 'react-router-dom';
import { CognitoAuthentication } from '../../cognito/cognito-helper';
import { useEffect, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { getText } from '../../translate/text';
import { getUserByTempPass, updateUserPass } from '@/APIs/usersAPIs';

export function EnforceChangePassword() {
  const location = useLocation();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');

  // Visibility states
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [email, setEmail] = useState('');

  const id = location.pathname.split('/').pop();
  const prefix = import.meta.env.VITE_REACT_APP_TEMP_PASS_PREFIX;
  const suffix = import.meta.env.VITE_REACT_APP_TEMP_PASS_SUFFIX;

  let trimmedId = null;
  if (id && id.startsWith(prefix) && id.includes(suffix)) {
    const start = id.indexOf(prefix);
    const end = id.indexOf(suffix);
    trimmedId = id.substring(start + prefix.length, end);
  } else if (id && id.startsWith(prefix)) {
    // fallback if suffix is missing
    trimmedId = id.substring(prefix.length);
  }

  useEffect(() => {
    const fetchUser = async () => {
      await handleGetUserByTempPass();
    };
    fetchUser();
  }, [trimmedId]);

  const handleGetUserByTempPass = async () => {
    try {
      const response = await getUserByTempPass(trimmedId);
      setEmail(response.data.email);
    } catch (error) {
      console.error('Error toggling specialty status:', error);
    }
  };

  const handlePasswordChange = async (event) => {
    event.preventDefault();

    if (newPassword !== confirmPassword) {
      setError(getText('new_passwords_do_not_match'));
      return;
    }

    try {
      const res = await CognitoAuthentication().forceResetPass(email, trimmedId, newPassword);
      if (res) {
        await updateUserPass(email);
        navigate('/');
      } else {
        setError(getText('password_change_failed'));
      }
    } catch (error) {
      setError(error.message || getText('an_unexpected_error_occured'));
    }
  };

  return (
    <div className="min-h-screen flex relative">
      {/* Centered Form Section */}
      <div className="w-1/2 flex flex-col justify-center px-16 bg-white dark:bg-neutral-900 relative">
        <div className="space-y-8">
          <div className="text-center mt-12">
            <h2 className="text-3xl font-extrabold text-neutral-900 dark:text-white mb-2">
              {getText('enforce_change_password')}
            </h2>
            <p className="text-neutral-600 dark:text-neutral-400">
              {getText('enter_your_old_password_and_new_password_twice_to_confirm')}
            </p>
          </div>

          <form onSubmit={handlePasswordChange} className="space-y-6">
            {/* New Password Input */}
            <div className="relative">
              <label htmlFor="new-password" className="form-label">
                {getText('new_password')}
              </label>
              <input
                id="new-password"
                type={showNew ? 'text' : 'password'}
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="form-input shadow-sm pr-10"
              />
              <span
                onClick={() => setShowNew(!showNew)}
                className="absolute right-3 top-9 cursor-pointer text-neutral-500"
              >
                {showNew ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </span>
            </div>

            {/* Confirm New Password Input */}
            <div className="relative">
              <label htmlFor="confirm-password" className="form-label">
                {getText('confirm_new_password')}
              </label>
              <input
                id="confirm-password"
                type={showConfirm ? 'text' : 'password'}
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="form-input shadow-sm pr-10"
              />
              <span
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-3 top-9 cursor-pointer text-neutral-500"
              >
                {showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </span>
            </div>

            {error && <p className="text-red-500 text-sm">{error}</p>}

            {/* Submit Button */}
            <button type="submit" className="btn-primary w-full">
              {getText('change_password')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
